/**
 * Adjust taxable income and tax bands based on salary.
 *
 * For Scottish taxpayers with partial personal allowance loss:
 *   1. Calculate the lost PA.
 *   2. Reduce the 0% band by that amount.
 *   3. Apply the adjusted bands from the config to the remainder.
 *
 * For UK (non-Scottish) residents:
 *   1. Calculate the lost PA.
 *   2. Reduce the 0% band by that amount.
 *   3. Add the lost allowance to the higher rate (40%) band breadth.
 *   4. Keep all other bands unchanged.
 *
 * @param {number} salaryPlusOvertimeMinusPension - The adjusted salary.
 * @param {Object} config - Configuration object containing tax band info.
 *   (For Scottish taxpayers, config.SCOTTISH should be true.)
 * @param {Function} calculateLostPersonalAllowance - Function to calculate the lost allowance.
 * @returns {Object} An object with taxBandsToUse.
 */
export function adjustTaxBands(
  salaryPlusOvertimeMinusPension,
  config,
  calculateLostPersonalAllowance
) {
  // Safety check for config
  if (!config || !config.TAX_BANDS || !config.ADJUSTED_TAX_BANDS) {
    return { taxBandsToUse: [] };
  }

  let taxBandsToUse;

  if (config.SCOTTISH) {
    // Scottish taxpayers: apply the logic for partial PA loss.
    if (salaryPlusOvertimeMinusPension <= 100000) {
      taxBandsToUse = config.TAX_BANDS;
    } else if (salaryPlusOvertimeMinusPension < 125140) {
      // Partial loss of PA: calculate the lost allowance.
      const { lostAllowance } = calculateLostPersonalAllowance(
        salaryPlusOvertimeMinusPension,
        config
      );
      // Reduce the 0% band by the lost allowance.
      const remainingTaxFree = config.PERSONAL_ALLOWANCE - lostAllowance;
      // Then, for the remainder of the income, apply the adjusted bands.
      taxBandsToUse = [
        { breadth: remainingTaxFree, rate: 0 },
        ...config.ADJUSTED_TAX_BANDS
      ];
    } else {
      // Full loss of PA – use the fully adjusted tax bands.
      taxBandsToUse = config.ADJUSTED_TAX_BANDS;
    }
  } else {
    // Non-Scottish (UK) logic - handles any number of tax bands
    if (salaryPlusOvertimeMinusPension <= 100000) {
      taxBandsToUse = config.TAX_BANDS;
    } else if (salaryPlusOvertimeMinusPension < 125140) {
      const { lostAllowance } = calculateLostPersonalAllowance(
        salaryPlusOvertimeMinusPension,
        config
      );

      // Build adjusted bands dynamically to support any number of bands
      taxBandsToUse = buildUKPartialLossBands(config.TAX_BANDS, lostAllowance, config.PERSONAL_ALLOWANCE);
    } else {
      taxBandsToUse = config.ADJUSTED_TAX_BANDS;
    }
  }

  return { taxBandsToUse };
}

/**
 * Builds UK tax bands for partial personal allowance loss scenarios.
 *
 * When personal allowance is partially lost (income £100k-£125,140):
 * - The 0% band shrinks by the lost amount
 * - The higher rate (40%) band expands by the lost amount
 * - All other bands remain unchanged
 *
 * This function works with any number of tax bands, not just the current 4-band system.
 *
 * @param {Array} taxBands - Original tax bands array
 * @param {number} lostAllowance - Amount of personal allowance lost
 * @param {number} personalAllowance - Full personal allowance amount
 * @returns {Array} Adjusted tax bands
 */
function buildUKPartialLossBands(taxBands, lostAllowance, personalAllowance) {
  if (!taxBands || taxBands.length < 2) {
    return [];
  }

  // Find the higher rate band (40% rate) - this is where lost allowance gets added
  // In UK tax system, the 40% band absorbs the lost personal allowance
  const HIGHER_RATE = 40;
  const higherRateBandIndex = taxBands.findIndex(band => band.rate === HIGHER_RATE);

  // If no 40% band found, fall back to adding lost allowance to the second-to-last band
  // This handles hypothetical future tax systems
  const bandToExpandIndex = higherRateBandIndex !== -1
    ? higherRateBandIndex
    : taxBands.length - 2;

  // Build the adjusted bands array
  const adjustedBands = taxBands.map((band, index) => {
    if (index === 0) {
      // First band (0%): reduce by lost allowance
      return {
        breadth: personalAllowance - lostAllowance,
        rate: 0
      };
    } else if (index === bandToExpandIndex) {
      // Higher rate band: expand by lost allowance
      return {
        breadth: band.breadth + lostAllowance,
        rate: band.rate
      };
    } else {
      // All other bands: unchanged
      return {
        breadth: band.breadth,
        rate: band.rate
      };
    }
  });

  return adjustedBands;
}
